// src/components/despesas/DespesasList.jsx
import React, { useState, useEffect, useCallback } from 'react';
import api from '../../api/api';
import './DespesasList.scss';

const CATEGORIAS = ['aluguel', 'salario', 'comissao', 'utilidades', 'outros'];
const STATUS_OPCOES = ['pendente', 'pago', 'cancelado'];

const formatCurrency = (v) =>
  Number(v || 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

const formatDate = (d) =>
  d ? new Date(d).toLocaleDateString('pt-BR') : '—';

const FORM_INITIAL = {
  descricao: '',
  categoria: 'outros',
  valor: '',
  data_vencimento: '',
  data_pagamento: '',
  status: 'pendente',
  recorrente: false,
  recorrencia_meses: 1,
  observacoes: '',
};

const DespesasList = ({ onDespesaChange }) => {
  const [despesas, setDespesas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [formData, setFormData] = useState(FORM_INITIAL);
  const [filtros, setFiltros] = useState({
    categoria: '',
    status: '',
    dataInicio: '',
    dataFim: '',
  });

  const fetchDespesas = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      if (filtros.categoria) params.categoria = filtros.categoria;
      if (filtros.status) params.status = filtros.status;
      if (filtros.dataInicio) params.dataInicio = filtros.dataInicio;
      if (filtros.dataFim) params.dataFim = filtros.dataFim;
      const res = await api.get('/despesas', { params });
      setDespesas(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filtros]);

  useEffect(() => {
    fetchDespesas();
  }, [fetchDespesas]);

  const handleChange = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const payload = {
        ...formData,
        valor: parseFloat(formData.valor),
        recorrencia_meses: formData.recorrente ? formData.recorrencia_meses : null,
      };
      if (editingId) {
        await api.put(`/despesas/${editingId}`, payload);
      } else {
        await api.post('/despesas', payload);
      }
      resetForm();
      fetchDespesas();
      if (onDespesaChange) onDespesaChange();
    } catch (err) {
      console.error(err);
      alert('Erro ao salvar despesa');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Excluir esta despesa?')) return;
    try {
      await api.delete(`/despesas/${id}`);
      fetchDespesas();
      if (onDespesaChange) onDespesaChange();
    } catch (err) {
      console.error(err);
      alert('Erro ao excluir');
    }
  };

  const resetForm = () => {
    setFormData(FORM_INITIAL);
    setEditingId(null);
    setShowModal(false);
  };

  const openEdit = (desp) => {
    setEditingId(desp.id);
    setFormData({
      descricao: desp.descricao ?? '',
      categoria: desp.categoria ?? 'outros',
      valor: desp.valor ?? '',
      data_vencimento: desp.data_vencimento?.slice(0, 10) ?? '',
      data_pagamento: desp.data_pagamento?.slice(0, 10) ?? '',
      status: desp.status ?? 'pendente',
      recorrente: desp.recorrente ?? false,
      recorrencia_meses: desp.recorrencia_meses ?? 1,
      observacoes: desp.observacoes ?? '',
    });
    setShowModal(true);
  };

  return (
    <div className="despesas-list">

      {/* ── Toolbar ───────────────────────────────────────────────── */}
      <div className="despesas-list__toolbar">
        <button className="btn-add" onClick={() => setShowModal(true)}>
          + Nova Despesa
        </button>

        <div className="despesas-list__filtros">
          <select
            value={filtros.categoria}
            onChange={(e) => setFiltros({ ...filtros, categoria: e.target.value })}
          >
            <option value="">Todas categorias</option>
            {CATEGORIAS.map((cat) => (
              <option key={cat} value={cat}>
                {cat.charAt(0).toUpperCase() + cat.slice(1)}
              </option>
            ))}
          </select>

          <select
            value={filtros.status}
            onChange={(e) => setFiltros({ ...filtros, status: e.target.value })}
          >
            <option value="">Todos status</option>
            {STATUS_OPCOES.map((st) => (
              <option key={st} value={st}>
                {st.charAt(0).toUpperCase() + st.slice(1)}
              </option>
            ))}
          </select>

          <input
            type="date"
            value={filtros.dataInicio}
            onChange={(e) => setFiltros({ ...filtros, dataInicio: e.target.value })}
          />
          <input
            type="date"
            value={filtros.dataFim}
            onChange={(e) => setFiltros({ ...filtros, dataFim: e.target.value })}
          />

          <button
            className="btn-clear"
            onClick={() => setFiltros({ categoria: '', status: '', dataInicio: '', dataFim: '' })}
          >
            Limpar
          </button>
        </div>
      </div>

      {/* ── Tabela ────────────────────────────────────────────────── */}
      <div className="table-wrapper">
        <table className="despesas-table">
          <thead>
            <tr>
              <th>Descrição</th>
              <th>Categoria</th>
              <th>Valor</th>
              <th>Vencimento</th>
              <th>Pagamento</th>
              <th>Status</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="7">
                  <div className="despesas-empty">
                    <span className="despesas-empty__icon">⏳</span>
                    Carregando...
                  </div>
                </td>
              </tr>
            ) : despesas.length === 0 ? (
              <tr>
                <td colSpan="7">
                  <div className="despesas-empty">
                    <span className="despesas-empty__icon">📭</span>
                    Nenhuma despesa encontrada.
                  </div>
                </td>
              </tr>
            ) : (
              despesas.map((d) => (
                <tr key={d.id}>
                  <td className="col-descricao" data-label="Descrição">
                    {d.descricao}
                  </td>
                  <td data-label="Categoria">
                    {d.categoria.charAt(0).toUpperCase() + d.categoria.slice(1)}
                  </td>
                  <td className="col-valor" data-label="Valor">
                    {formatCurrency(d.valor)}
                  </td>
                  <td className="col-data" data-label="Vencimento">
                    {formatDate(d.data_vencimento)}
                  </td>
                  <td className="col-data" data-label="Pagamento">
                    {formatDate(d.data_pagamento)}
                  </td>
                  <td data-label="Status">
                    <span className={`status-badge status-badge--${d.status}`}>
                      {d.status}
                    </span>
                  </td>
                  <td className="col-acoes" data-label="Ações">
                    <button
                      className="btn-icon"
                      title="Editar"
                      onClick={() => openEdit(d)}
                    >
                      ✏️
                    </button>
                    <button
                      className="btn-icon"
                      title="Excluir"
                      onClick={() => handleDelete(d.id)}
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* ── Modal ─────────────────────────────────────────────────── */}
      {showModal && (
        <div className="modal-overlay" onClick={resetForm}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>

            {/* Cabeçalho */}
            <div className="modal-header">
              <h3>{editingId ? 'Editar Despesa' : 'Nova Despesa'}</h3>
              <button className="modal-close" onClick={resetForm}>×</button>
            </div>

            {/* Formulário */}
            <form onSubmit={handleSubmit}>
              <div className="modal-body">

                <div className="form-group">
                  <label>Descrição *</label>
                  <input
                    type="text"
                    required
                    placeholder="Ex: Aluguel da loja"
                    value={formData.descricao}
                    onChange={(e) => handleChange('descricao', e.target.value)}
                  />
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Categoria *</label>
                    <select
                      value={formData.categoria}
                      onChange={(e) => handleChange('categoria', e.target.value)}
                    >
                      {CATEGORIAS.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat.charAt(0).toUpperCase() + cat.slice(1)}
                        </option>
                      ))}
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Valor * (R$)</label>
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      required
                      placeholder="0,00"
                      value={formData.valor}
                      onChange={(e) => handleChange('valor', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label>Vencimento</label>
                    <input
                      type="date"
                      value={formData.data_vencimento}
                      onChange={(e) => handleChange('data_vencimento', e.target.value)}
                    />
                  </div>
                  <div className="form-group">
                    <label>Data Pagamento</label>
                    <input
                      type="date"
                      value={formData.data_pagamento}
                      onChange={(e) => handleChange('data_pagamento', e.target.value)}
                    />
                  </div>
                </div>

                <div className="form-group">
                  <label>Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => handleChange('status', e.target.value)}
                  >
                    {STATUS_OPCOES.map((st) => (
                      <option key={st} value={st}>
                        {st.charAt(0).toUpperCase() + st.slice(1)}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group form-group--checkbox">
                  <input
                    type="checkbox"
                    id="chk-recorrente"
                    checked={formData.recorrente}
                    onChange={(e) => handleChange('recorrente', e.target.checked)}
                  />
                  <label htmlFor="chk-recorrente">Despesa recorrente</label>
                </div>

                {formData.recorrente && (
                  <div className="form-group">
                    <label>Repetir a cada (meses)</label>
                    <input
                      type="number"
                      min="1"
                      value={formData.recorrencia_meses}
                      onChange={(e) =>
                        handleChange('recorrencia_meses', parseInt(e.target.value) || 1)
                      }
                    />
                  </div>
                )}

                <div className="form-group">
                  <label>Observações</label>
                  <textarea
                    rows="3"
                    placeholder="Informações adicionais..."
                    value={formData.observacoes}
                    onChange={(e) => handleChange('observacoes', e.target.value)}
                  />
                </div>
              </div>

              {/* Ações */}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={resetForm}>
                  Cancelar
                </button>
                <button type="submit" className="btn-save-modal" disabled={saving}>
                  {saving ? (
                    <>
                      <span className="btn-spinner" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </button>
              </div>
            </form>

          </div>
        </div>
      )}
    </div>
  );
};

export default DespesasList;